"use client";
import React from "react";
import { motion } from "motion/react";
import {
  ShoppingCart,
  Utensils,
  Laptop,
  Sparkles,
  Pill,
  Store,
  ArrowRight,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { ImageWithFallback } from "@/src/components/figma/ImageWithFallback";
import Link from "next/link";
import { GlassCard } from "@/src/components/GlassCard";

export const IndustriesPage: React.FC = () => {
  const { t } = useTranslation("common");
  const industries = [
    {
      icon: ShoppingCart,
      title: t("industriesPage.industries.ecommerce.title"),
      description: t("industriesPage.industries.ecommerce.description"),
      features: t("industriesPage.industries.ecommerce.features", {
        returnObjects: true,
      }) as string[],
      image:
        "https://images.unsplash.com/photo-1631010231931-d2c396b444ec?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxlY29tbWVyY2UlMjBzaGlwcGluZyUyMGJveGVzJTIwZGVsaXZlcnl8ZW58MXx8fHwxNzcxNjA3OTQ2fDA&ixlib=rb-4.1.0&q=80&w=1080",
    },
    {
      icon: Utensils,
      title: t("industriesPage.industries.foodBeverage.title"),
      description: t("industriesPage.industries.foodBeverage.description"),
      features: t("industriesPage.industries.foodBeverage.features", {
        returnObjects: true,
      }) as string[],
      image:
        "https://images.unsplash.com/photo-1766050588070-b4ec0134ff8d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxmb29kJTIwZ3JhZGUlMjBwYWNrYWdpbmclMjBjb250YWluZXJzfGVufDF8fHx8MTc3MTYwNzk0Nnww&ixlib=rb-4.1.0&q=80&w=1080",
    },
    {
      icon: Laptop,
      title: t("industriesPage.industries.electronics.title"),
      description: t("industriesPage.industries.electronics.description"),
      features: t("industriesPage.industries.electronics.features", {
        returnObjects: true,
      }) as string[],
      image:
        "https://images.unsplash.com/photo-1631010231888-777b6285ef84?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjdXN0b20lMjByZXRhaWwlMjBwcm9kdWN0JTIwYm94ZXN8ZW58MXx8fHwxNzcxNjA3OTQ2fDA&ixlib=rb-4.1.0&q=80&w=1080",
    },
    {
      icon: Sparkles,
      title: t("industriesPage.industries.cosmetics.title"),
      description: t("industriesPage.industries.cosmetics.description"),
      features: t("industriesPage.industries.cosmetics.features", {
        returnObjects: true,
      }) as string[],
      image:
        "https://images.unsplash.com/photo-1755606396356-bdd7cd95df75?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzdXN0YWluYWJsZSUyMGVjbyUyMHBhY2thZ2luZyUyMG1hdGVyaWFsc3xlbnwxfHx8fDE3NzE2MDc5NDV8MA&ixlib=rb-4.1.0&q=80&w=1080",
    },
    {
      icon: Pill,
      title: t("industriesPage.industries.pharmaceuticals.title"),
      description: t("industriesPage.industries.pharmaceuticals.description"),
      features: t("industriesPage.industries.pharmaceuticals.features", {
        returnObjects: true,
      }) as string[],
      image:
        "https://images.unsplash.com/photo-1739204618173-3e89def7140f?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb3JydWdhdGVkJTIwY2FyZGJvYXJkJTIwYm94ZXMlMjB3YXJlaG91c2V8ZW58MXx8fHwxNzcxNjA3OTQ1fDA&ixlib=rb-4.1.0&q=80&w=1080",
    },
    {
      icon: Store,
      title: t("industriesPage.industries.retail.title"),
      description: t("industriesPage.industries.retail.description"),
      features: t("industriesPage.industries.retail.features", {
        returnObjects: true,
      }) as string[],
      image:
        "https://images.unsplash.com/photo-1635961179148-3d886568775c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwcGFja2FnaW5nJTIwZmFjdG9yeSUyMG1vZGVybnxlbnwxfHx8fDE3NzE2MDc5NDV8MA&ixlib=rb-4.1.0&q=80&w=1080",
    },
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-24">
        <div className="absolute inset-0 z-0">
          <ImageWithFallback
            src="https://images.unsplash.com/photo-1635961179148-3d886568775c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwcGFja2FnaW5nJTIwZmFjdG9yeSUyMG1vZGVybnxlbnwxfHx8fDE3NzE2MDc5NDV8MA&ixlib=rb-4.1.0&q=80&w=1080"
            alt="Industries We Serve"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-walnut-900/90 via-walnut-800/80 to-walnut-600/70 dark:from-black/90 dark:via-walnut-900/85 dark:to-walnut-800/75" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto text-center py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
              {t("industriesPage.hero.title")}
            </h1>
            <p className="text-xl text-walnut-100 max-w-3xl mx-auto">
              {t("industriesPage.hero.subtitle")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-20">
          {industries.map((industry, index) => (
            <div
              key={index}
              className={`grid md:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? "md:flex-row-reverse" : ""}`}
            >
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={index % 2 === 1 ? "md:order-2" : ""}
              >
                <GlassCard className="p-0 overflow-hidden">
                  <ImageWithFallback
                    src={industry.image}
                    alt={industry.title}
                    className="w-full h-96 object-cover"
                  />
                </GlassCard>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={index % 2 === 1 ? "md:order-1" : ""}
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-walnut-500 to-walnut-700 flex items-center justify-center mb-6">
                  <industry.icon className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-4xl font-bold text-walnut-900 dark:text-white mb-4">
                  {industry.title}
                </h2>
                <p className="text-lg text-walnut-700 dark:text-walnut-300 mb-6">
                  {industry.description}
                </p>

                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-walnut-900 dark:text-white mb-3">
                    {t("industriesPage.keyFeatures")}
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {(industry.features as string[]).map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-walnut-600 dark:bg-walnut-400" />
                        <span className="text-walnut-700 dark:text-walnut-300">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <Link
                  href="/quote"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-walnut-600 to-walnut-700 dark:from-walnut-500 dark:to-walnut-600 text-white font-semibold hover:shadow-lg hover:shadow-walnut-500/30 transition-all duration-300 hover:scale-105"
                >
                  {t("industriesPage.getQuote")}
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </motion.div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <GlassCard className="p-12 text-center">
            <h2 className="text-4xl font-bold text-walnut-900 dark:text-white mb-6">
              {t("industriesPage.cta.title")}
            </h2>
            <p className="text-lg text-walnut-700 dark:text-walnut-300 mb-8 max-w-2xl mx-auto">
              {t("industriesPage.cta.subtitle")}
            </p>
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-walnut-600 to-walnut-700 dark:from-walnut-500 dark:to-walnut-600 text-white font-semibold hover:shadow-lg hover:shadow-walnut-500/30 transition-all duration-300 hover:scale-105"
            >
              {t("industriesPage.cta.button")}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </GlassCard>
        </div>
      </section>
    </div>
  );
};
